import _ from 'lodash';
import Backbone from 'backbone';
import Syphon from 'backbone.syphon';
import Radio from 'backbone.radio';

import OrderTypes from '@/enums/OrderTypes';
import DocumentDownloadTypes from '@/enums/DocumentDownloadTypes';
import DocumentTypes from '@/enums/DocumentTypes';
import OrderFileUploadTmpl from '@/fileupload/OrderFileUploadTmpl.hbs';
import showErrorMessage from '@/util/showErrorMessage';
import showSuccessMessage from '@/util/ShowSuccessMessage';
import ValidFileTypeUtil from '@/util/ValidFileTypeUtil';
import AdHocAttachmentsView from '@/views/AdHocAttachmentsView';
import OrderSummaryAttachmentView from '@/views/OrderSummaryAttachmentView/OrderSummaryAttachmentView';
import FundingAttachmentView from '@/views/FundingAttachmentsView/FundingAttachmentsView';
import AttachmentsCollectionView from '@/views/AttachmentsCollectionView';
import { formatDisplayDate } from '@/util/MomentUtils';

import SaveCustomerOrderAttachments from '../../services/SaveCustomerOrderAttachments';

import SubmittableOrderView from '../SubmittableOrderView/SubmittableOrderView';

import tmplOrderAttachmentsView from './OrderAttachmentsView.hbs';
import OrderStatuses from '@/enums/OrderStatuses';

const OrderAttachmentsView = SubmittableOrderView.extend({
  template: tmplOrderAttachmentsView,

  className: 'order-attachments-view',

  ui: {
    additionalDocumentsQuestion: '#additionalDocumentsQuestion',
    additionalDocuments: '.additional-documents',
    attachments: '.attachments',
    adhocAttachments: '.adhoc-attachments',
    errRequired: '.err-required',
    orderSummaryAttachment: '.order-summary-attachment',
    fundingAttachment: '.funding-attachment',
    errAdditionalDocuments: '.err-additional-documents'
  },

  regions: {
    attachments: '@ui.attachments',
    adhocAttachments: '@ui.adhocAttachments',
    orderSummaryAttachment: '@ui.orderSummaryAttachment',
    fundingAttachment: '@ui.fundingAttachment'
  },

  events: {
    'change @ui.additionalDocumentsQuestion': 'onChangeAdditionalDocuments'
  },

  childViewEvents: {
    'upload:complete': 'onUploadComplete',
    'file:deleted': 'onFileDelete'
  },

  initialize(options) {
    const isReadOnly = this.model.get('isReadOnly');
    let documentTypes = _.filter(this.model.get('documentTypes'), (documentType) => {
      return documentType.documentTypeId !== DocumentTypes.COMMUNICATION && documentType.documentTypeId !== DocumentTypes.NOTE && documentType.isFunding !== '1';
    });

    const attachments = this.model.get('attachments');
    //remove chess attachment if it hasn't been uploaded
    if (!_.find(attachments, { documentTypeId: DocumentTypes.CHESS })) {
      documentTypes = _.filter(documentTypes, (documentType) => {
        return documentType.documentTypeId !== DocumentTypes.CHESS;
      });
    }

    // Hide customer responsibilities acknowledgment from external users if it hasn't been generated
    if ([OrderStatuses.INTERNAL_DRAFT, OrderStatuses.DRAFT].includes(this.model.get('orderDetails').statusId)
      && !_.find(attachments, { documentTypeId: DocumentTypes.CUST_RESP })) {
      documentTypes = _.filter(documentTypes, (documentType) => {
        return documentType.documentTypeId !== DocumentTypes.CUST_RESP;
      });
    }


    const documentMapping = this.mapDocs(documentTypes, attachments, options, isReadOnly);
    this.model.set('documentMapping', documentMapping);

    this.listenTo(Radio.channel('order'), 'file:added', (coll) => {
      this.mapDocs(documentTypes, attachments, options, isReadOnly);
    });

    if (this.model.get('isA3') && this.model.get('orderSummaryExists')) {
      this.showOrderSummaryAttachmentView();
    }
  },

  mapDocs(documentTypes, attachments, options, isReadOnly) {
    const documentMapping = _.map(documentTypes, (documentType) => {
      const { documentTypeDescription, documentTypeId, attachmentId, defaultFileName } = documentType;
      const attachment = _.find(attachments, (attachment) => attachment.documentTypeId === documentTypeId) || null;
      const isRequired = documentType?.isRequired === '1';
      const setReadOnly = isReadOnly || [DocumentTypes.ORDER_SUMMARY, DocumentTypes.CUST_RESP].includes(documentTypeId);

      return {
        label: {
          text: documentTypeDescription,
          fileName: documentType.defaultFileName,
          isDocument: documentType.attachmentId,
          downloadType: DocumentDownloadTypes.TEMPLATE,
          attachmentId
        },
        attachment: {
          isReadOnly: true,
          attachmentName: attachment?.fileName,
          attachmentId: attachment?.id,
          fileUploadTmpl: OrderFileUploadTmpl,
          pathToFileId: 'html.head.script.Envelope.Body.UploadOrderAttachmentResponse.AttachmentID.__text',
          defaultFileName,
          validator: ValidFileTypeUtil,
          metadata: {
            attachmentId: attachment?.id,
            fileName: attachment?.fileName,
            orderId: options?.orderId,
            documentTypeId
          }
        },
        isRequired,
        isReadOnly: setReadOnly
      };
    });
    return documentMapping;
  },

  onRender() {
    const numAttachments = this.model.get('attachments').length + this.model.get('adHocAttachments').length;
    this.setNumAttachments(numAttachments);

    this.ui.errRequired.hide();
    this.ui.errAdditionalDocuments.hide();

    if (this.model.get('orderDetails').orderTypeId === OrderTypes.RELOCATION) {
      this.showAdditionalDocuments = true;
      this.ui.additionalDocumentsQuestion.hide();
      this.showAdditionalAttachments();
    } else {
      this.showFundingAttachmentView(this.model.get('isReadOnly'));
      this.onChangeAdditionalDocuments();
    }
  },

  onUploadComplete(model) {
    this.setNumAttachments(this.model.get('numAttachments') + 1);
  },

  onFileDelete(model) {
    this.setNumAttachments(this.model.get('numAttachments') - 1);
  },

  setNumAttachments(numAttachments) {
    this.model.set('numAttachments', numAttachments);
    const isReadOnly = this.model.get('isReadOnly');
    this.ui.additionalDocumentsQuestion.find(':input').prop('disabled', isReadOnly || numAttachments);
  },

  onChangeAdditionalDocuments(e) {
    e?.preventDefault();

    this.showAdditionalDocuments = $(`input[name="additionalDocumentsRequired"]:checked`).val() ?
      $(`input[name="additionalDocumentsRequired"]:checked`).val() === '1' :
      this.model.get('orderAttachmentData')?.additionalDocumentsRequired === '1';

    if (this.showAdditionalDocuments) {
      this.showAdditionalAttachments();
    } else {
      this.ui.additionalDocuments.hide();
    }
    this.ui.errAdditionalDocuments.hide();
  },

  showAdditionalAttachments() {
    this.ui.additionalDocuments.show();
    this.showAttachmentsView();
    this.showAdHocAttachmentsView();
  },

  showAttachmentsView() {
    this.collection = new Backbone.Collection(this.model.get('documentMapping'));
    this.attachmentsCollectionView = new AttachmentsCollectionView({
      collection: this.collection
    });
    this.showChildView('attachments', this.attachmentsCollectionView);
  },

  showAdHocAttachmentsView() {
    const attachments = this.model.get('adHocAttachments');
    const adhocAttachmentsModel = new Backbone.Model({
      attachments,
      editable: !this.model.get('isReadOnly'),
      orderId: this.model.get('orderDetails')?.orderId
    });
    this.adHodAttachmentsView = new AdHocAttachmentsView({ model: adhocAttachmentsModel });
    this.showChildView('adhocAttachments', this.adHodAttachmentsView);
  },

  showOrderSummaryAttachmentView() {
    const attachment = this.model.get('orderSummaryAttachment')[0];
    if (attachment) {
      const orderSummaryAttachmentModel = new Backbone.Model({
        attachment,
        attachmentName: attachment?.fileName,
        generatedOn: formatDisplayDate(attachment?.uploadedOn),
        orderId: this.model.get('orderDetails')?.orderId,
        canGenerate: this.model.get('canGenerate')
      });
      this.showChildView(
        'orderSummaryAttachment',
        new OrderSummaryAttachmentView({ model: orderSummaryAttachmentModel })
      );
    }
  },

  showFundingAttachmentView(isReadOnly) {
    const attachments = this.model.get('fundingAttachments');
    const fundingDocumentTypes = _.filter(this.model.get('documentTypes'), (doc) => {
      return doc.isFunding === '1';
    });
    const displayFundingTypes = fundingDocumentTypes.filter(doc => doc.documentTypeId !== DocumentTypes['7600A'])
      .map(doc => ({
        id: doc.documentTypeId,
        name: doc.documentTypeDescription
      }));

    const orderAttachmentData = this.model.get('orderAttachmentData')

    const fundingAttachmentModel = new Backbone.Model({
      orderId: this.model.get('orderDetails')?.orderId,
      fundingDocumentTypes,
      displayFundingTypes,
      attachments,
      showFundingReasoning: this.model.get('showFundingReasoning'),
      disableFundingReasoning: this.model.get('disableFundingReasoning'),
      noFundingExplanation: this.model.get('noFundingExplanation'),
      fundingAmount: orderAttachmentData.fundingAmount,
      fundingTypeID: orderAttachmentData.fundingTypeID,
      gInvoicingOrderNumber: orderAttachmentData.gInvoicingOrderNumber,
      isReadOnly
    });
    this.fundingAttachmentsView = new FundingAttachmentView({ model: fundingAttachmentModel })
    this.showChildView('fundingAttachment', this.fundingAttachmentsView);
  },

  async validate({ calledOnSubmit } = {}) {
    let isValid = true;
    this.ui.errRequired.hide();
    this.ui.errAdditionalDocuments.hide();

    const answeredAdditionalDocumentsQuestion = $(`input[name="additionalDocumentsRequired"]:checked`).val();
    const isRelocation = this.model.get('orderDetails').orderTypeId === OrderTypes.RELOCATION
    if(!answeredAdditionalDocumentsQuestion && !isRelocation) {
      isValid = false;
      this.ui.errAdditionalDocuments.show();
    }

    if(this.showAdditionalDocuments) {
      for (const attachmentItemView of this.attachmentsCollectionView.children._views) {
        if (!attachmentItemView.validate()) {
          isValid = false;
          this.ui.errRequired.show();
        }
      }
    }

    if (this.fundingAttachmentsView) {
      isValid &&= await this.fundingAttachmentsView.validate(calledOnSubmit);
    }

    isValid ? this.trigger('no:errors') :this.trigger('has:errors');
    return isValid;
  },

  async onSave() {
    const isValid = await this.validate();
    if(!isValid) throw new Error();

    const orderId = this.model.get('orderDetails')?.orderId;
    const serialFormData = Syphon.serialize(this);

    try {
      await SaveCustomerOrderAttachments(orderId, serialFormData);
      this.showSaveSuccess();
    } catch(err) {
      this.showSaveError();
      throw err;
    }
  },

  showSaveError() {
    showErrorMessage({
      errorCode: 'saveError',
      errorTitle: 'Save Error',
      errorMessage:
        'An error occurred while saving this order. Try again or contact an administrator if your issue persists.'
    });
  },

  showSaveSuccess() {
    showSuccessMessage({
      code: 'saveSuccess',
      title: 'Save Success',
      message: 'This order has been saved successfully.',
      hideAfter: 5
    });
  }
});

export default OrderAttachmentsView;
